import { useEffect, useId, useState } from "react"

import { cn } from "../lib/cn"
import { useIsMobile } from "../lib/use-is-mobile"
import * as Icones from "./Icones"
import { NativeSelect } from "./NativeSelect"

const OPCOES_POR_PAGINA = [10, 20, 30, 40, 50]

const SETA_DE_PAGINA =
  "flex size-9 items-center justify-center rounded-md text-foreground transition-colors motion-reduce:transition-none hover:bg-muted disabled:cursor-not-allowed disabled:text-(--tinta-3) disabled:hover:bg-transparent md:size-8"

export function paginasVisiveis(
  paginaAtual: number,
  totalDePaginas: number,
  vizinhas: number,
): (number | "reticencias")[] {
  const itens: (number | "reticencias")[] = []
  for (let pagina = 1; pagina <= totalDePaginas; pagina += 1) {
    const perto = pagina >= paginaAtual - vizinhas && pagina <= paginaAtual + vizinhas
    if (pagina === 1 || pagina === totalDePaginas || perto) {
      itens.push(pagina)
    } else if (itens[itens.length - 1] !== "reticencias") {
      itens.push("reticencias")
    }
  }
  return itens
}

export type PropsDaPaginacao = {
  pagina: number
  porPagina: number
  total: number
  aoTrocarPagina: (pagina: number) => void
  aoTrocarPorPagina?: (porPagina: number) => void
  opcoesPorPagina?: number[]
  className?: string
}

export function Paginacao({
  pagina,
  porPagina,
  total,
  aoTrocarPagina,
  aoTrocarPorPagina,
  opcoesPorPagina = OPCOES_POR_PAGINA,
  className,
}: PropsDaPaginacao) {
  const idDoSeletor = useId()
  const noCelular = useIsMobile()
  const ultimaPagina = Math.max(1, Math.ceil(total / porPagina))
  const opcoes = [...new Set([...opcoesPorPagina, porPagina])].sort((a, b) => a - b)
  const comSeletor = aoTrocarPorPagina !== undefined && total > Math.min(...opcoes)
  const comPaginas = ultimaPagina > 1
  if (!comSeletor && !comPaginas) return null

  const primeiro = total === 0 ? 0 : (pagina - 1) * porPagina + 1
  const ultimo = Math.min(pagina * porPagina, total)

  return (
    <div
      data-slot="paginacao"
      className={cn("flex flex-wrap items-center justify-between gap-3 py-4", className)}
    >
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        {comSeletor && (
          <>
            <label htmlFor={idDoSeletor}>Itens por página</label>
            <NativeSelect
              id={idDoSeletor}
              className="w-20"
              value={porPagina}
              onChange={(evento) => aoTrocarPorPagina?.(Number(evento.target.value))}
            >
              {opcoes.map((opcao) => (
                <option key={opcao} value={opcao}>
                  {opcao}
                </option>
              ))}
            </NativeSelect>
          </>
        )}
        <span className="tabular-nums">
          {primeiro}–{ultimo} de {total}
        </span>
      </div>

      {comPaginas && (
        <nav aria-label="Paginação" className="ml-auto flex flex-wrap items-center gap-1">
          <button
            type="button"
            aria-label="Página anterior"
            title="Volta uma página"
            onClick={() => aoTrocarPagina(pagina - 1)}
            disabled={pagina <= 1}
            className={SETA_DE_PAGINA}
          >
            <Icones.Anterior tamanho={16} />
          </button>

          {paginasVisiveis(pagina, ultimaPagina, noCelular ? 1 : 2).map((item, indice) =>
            item === "reticencias" ? (
              <span
                key={`reticencias-${indice}`}
                data-slot="reticencias"
                className="px-1 text-muted-foreground"
              >
                <span aria-hidden="true">…</span>
                <span className="sr-only">Mais páginas</span>
              </span>
            ) : (
              <button
                key={item}
                type="button"
                aria-label={`Página ${item}`}
                aria-current={item === pagina ? "page" : undefined}
                onClick={() => aoTrocarPagina(item)}
                className={cn(
                  "flex size-9 items-center justify-center rounded-md text-sm font-medium tabular-nums transition-colors motion-reduce:transition-none md:size-8",
                  item === pagina
                    ? "bg-marca-selecionada text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {item}
              </button>
            ),
          )}

          <button
            type="button"
            aria-label="Próxima página"
            title="Avança uma página"
            onClick={() => aoTrocarPagina(pagina + 1)}
            disabled={pagina >= ultimaPagina}
            className={SETA_DE_PAGINA}
          >
            <Icones.Proxima tamanho={16} />
          </button>
        </nav>
      )}
    </div>
  )
}

export function usePaginacaoNaTela<T>(
  itens: readonly T[],
  porPaginaInicial = 10,
): { visiveis: T[]; paginacao: PropsDaPaginacao } {
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(porPaginaInicial)
  const ultima = Math.max(1, Math.ceil(itens.length / porPagina))
  const paginaValida = Math.min(pagina, ultima)

  useEffect(() => {
    if (pagina > ultima) definirPagina(ultima)
  }, [pagina, ultima])

  return {
    visiveis: itens.slice((paginaValida - 1) * porPagina, paginaValida * porPagina),
    paginacao: {
      pagina: paginaValida,
      porPagina,
      total: itens.length,
      aoTrocarPagina: definirPagina,
      aoTrocarPorPagina: (quantos) => {
        definirPorPagina(quantos)
        definirPagina(1)
      },
    },
  }
}
